import React, { useEffect, useState } from 'react';
import { RecoilState, useRecoilState } from 'recoil';
import { Box, Grid } from '@mui/material';
import { Vector3 } from 'features/morphing/state/parameterAtoms';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { CustomNumberField } from 'shared/ui/CustomNumberField';

interface Props {
  vectorState: RecoilState<Vector3>;
  label: string;
  minVectorState?: RecoilState<Vector3>;
  maxVectorState?: RecoilState<Vector3>;
  labelXs?: number;
  fieldXs?: number;
  backgroundColor?: string;
}

export const VectorForm: React.FC<Props> = ({
  vectorState,
  label,
  minVectorState = null,
  maxVectorState = null,
  labelXs = 3,
  fieldXs = 3,
  backgroundColor = "#1e1e1e"
}) => {
  const [vector, setVector] = useRecoilState(vectorState);
  const [minVector] = useRecoilState(minVectorState || vectorState); // Default to current vector state if minVectorState is null
  const [maxVector] = useRecoilState(maxVectorState || vectorState); // Default to current vector state if maxVectorState is null
  const [x, setX] = useState<string>(String(vector.x));
  const [y, setY] = useState<string>(String(vector.y));
  const [z, setZ] = useState<string>(String(vector.z));

  useEffect(() => {
    setX(String(vector.x));
    setY(String(vector.y));
    setZ(String(vector.z));
  }, [vector]);

  const handleChange = (axis: 'x' | 'y' | 'z') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Update the corresponding local state
    if (axis === 'x') setX(inputValue);
    if (axis === 'y') setY(inputValue);
    if (axis === 'z') setZ(inputValue);

    // Parse the input value as a number
    const numericValue = parseInt(inputValue, 10);

    // If the input is a valid number, update the vector state
    if (!isNaN(numericValue)) {
      setVector(prevVector => ({
        ...prevVector,
        [axis]: numericValue,
      }));
    }
  };

  return (
    <Box component="div" sx={{ paddingTop: 1 }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={labelXs}>
          <CustomTypography>
            {label}
          </CustomTypography>
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="X"
            type="number"
            fullWidth
            value={x}
            onChange={handleChange('x')}
            inputProps={{
              min: minVectorState ? minVector.x : undefined,
              max: maxVectorState ? maxVector.x : undefined,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="Y"
            fullWidth
            value={y}
            onChange={handleChange('y')}
            type="number"
            inputProps={{
              min: minVectorState ? minVector.y : undefined,
              max: maxVectorState ? maxVector.y : undefined,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="Z"
            fullWidth
            value={z}
            onChange={handleChange('z')}
            type="number"
            inputProps={{
              min: minVectorState ? minVector.z : undefined,
              max: maxVectorState ? maxVector.z : undefined,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};
